import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useCentralizedMobileDetection } from '@/hooks/useCentralizedMobileDetection';
import { useDialogSizing } from '@/hooks/useDialogSizing';
import { StandardDialogTemplateProps } from './types';
import {
  StandardDialogBackdrop,
  StandardDialogHeader,
  StandardDialogContent,
  StandardDialogAudioPlayer,
  StandardDialogFooter,
} from './components';
import { useDialogGestures, useDialogAccessibility, useDialogTheme } from './hooks';
import { LAYERS } from '@/utils/layers';

const StandardDialogTemplate = React.forwardRef<HTMLDivElement, StandardDialogTemplateProps>(
  (props, ref) => {
    const {
      isOpen,
      onClose,
      children,

      // Gesture system
      gesturePreset = 'disabled',
      gestureConfig,
      gestureCallbacks = {},

      // Expansion system
      isExpanded = false,
      onToggleExpanded,

      // Component configurations
      audioConfig,
      backdropConfig,
      headerConfig,
      footerConfig,

      // Styling and positioning
      dialogStyle,
      className,
      customZIndex,
      excludeHeaderFooter = false,

      // Data and context
      prayer,

      // Accessibility
      ariaLabel = 'Dialog',
      ariaDescribedBy,

      // Additional props
      ...additionalProps
    } = props;

    // Centralized mobile detection - auto-configure dialog behavior
    const detection = useCentralizedMobileDetection();

    // Auto-configure dialog position based on device type
    const resolvedDialogPosition = dialogStyle?.dialogPosition || detection.dialogPosition;

    // Dialog sizing integration - get the calculated styles
    const { 
      collapsedStyle, 
      expandedStyle,
      getPositionStyle 
    } = useDialogSizing({
      excludeHeaderFooter,
      enableDebug: false,
    });

    // Use the calculated styles based on expansion state
    const dialogContentStyle = isExpanded ? expandedStyle : collapsedStyle;

    // Gesture system integration
    const { swipeHandlers, swipeState } = useDialogGestures({
      gesturePreset,
      gestureConfig,
      gestureCallbacks: {
        onClose,
        onExpand: onToggleExpanded,
        onCollapse: onToggleExpanded,
        onCollapseOrClose: onClose,
        ...gestureCallbacks,
      },
      enabled: isOpen,
    });

    // Accessibility integration
    const { dialogRef, dialogProps } = useDialogAccessibility({
      isOpen,
      onClose,
      ariaLabel,
      ariaDescribedBy,
    });

    // Theme integration
    const { dialogClasses: themeClasses } = useDialogTheme({
      isExpanded,
      gestureActive: swipeState?.isActive,
      className,
      dialogPosition: resolvedDialogPosition,
      detection, // Pass detection to theme hook
    });

    // Dialog positioning and styling using useDialogSizing
    const dialogPositionStyle = useMemo(() => {
      // Map dialog position to sizing type
      const sizingType = resolvedDialogPosition === 'bottom' ? 'bottom-sheet' : 'centered-modal';
      return getPositionStyle(sizingType, isExpanded);
    }, [getPositionStyle, resolvedDialogPosition, isExpanded]);

    const dialogClasses = useMemo(() => {
      const baseClasses = 'fixed outline-none';
      return cn(baseClasses);
    }, []);

    // FIX: use unified z-index constant, not a magic number
    const dialogZIndex = customZIndex || dialogStyle?.zIndex || LAYERS.DIALOG_CONTENT;

    if (!isOpen) return null;

    return (
      <>
        {/* Backdrop */}
        {backdropConfig?.enabled && (
          <StandardDialogBackdrop
            {...backdropConfig}
            isOpen={isOpen}
            style={{ zIndex: dialogZIndex - 1 }}
            detection={detection}
          />
        )}

        {/* Main Dialog */}
        <div
          {...dialogProps}
          ref={(node) => {
            // Merge the external ref with dialogRef
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
            if (dialogRef && 'current' in dialogRef) {
              (dialogRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
          }}
          className={dialogClasses}
          style={{
            zIndex: dialogZIndex,
            // Use bottom sheet positioning from useDialogSizing
            ...dialogPositionStyle,
          }}
          aria-modal={true}
          {...swipeHandlers}
          {...additionalProps}
        >
          <div
            className={cn(
              'flex flex-col relative',
              themeClasses
            )}
            style={dialogContentStyle}
          >
            {/* Header */}
            {!excludeHeaderFooter && headerConfig && (
              <StandardDialogHeader
                {...headerConfig}
                prayer={prayer}
                isExpanded={isExpanded}
                onToggle={onToggleExpanded}
                onClose={onClose}
                swipeState={swipeState}
                detection={detection}
              />
            )}

            {/* Audio Player */}
            {audioConfig?.enabled && (
              <StandardDialogAudioPlayer
                {...audioConfig}
                prayer={prayer}
                onExpansionChange={onToggleExpanded}
              />
            )}

            {/* Content */}
            <StandardDialogContent isExpanded={isExpanded} scrollable={true} detection={detection}>
              {children}
            </StandardDialogContent>

            {/* Footer */}
            {!excludeHeaderFooter && footerConfig && <StandardDialogFooter {...footerConfig} />}
          </div>
        </div>
      </>
    );
  }
);

StandardDialogTemplate.displayName = 'StandardDialogTemplate';

export default StandardDialogTemplate;
export { StandardDialogTemplate };

export * from './components';
export * from './hooks';
export * from './types';
